import React, { useContext } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form as FormFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import trans from "../../utils";
import { popupcontext } from "../../context/Popupscontext";
import Popup from "../Helpers/Popup/Popup";
import PopupTitle from "../Helpers/Popup/PopupParts/PopupTitle";
import Close from "../Helpers/CloseBtn/Close";
import Button from "../Helpers/Button/Button";
import { Input } from "../Helpers/Input/Input";
import { InputSelect } from "../Helpers/Input/RefactoredInput";
import {
	useAssignProjectsMutation,
	useEditCompanyAssignedProjectsMutation,
	useGetCompanyProjectTradesQuery,
	useGetCompanyTradesQuery,
} from "../../Redux/service/Companies/Company";
import {
	useGetPOIProjectsQuery,
	useGetPOIZonesByParentIdQuery,
} from "../../Redux/service/POIS/POIs";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

// Component for zones dropdown that depends on selected project
const ProjectZonesSelect = ({
	projectId,
	value,
	onChange,
	errors,
	touched,
}) => {
	const { data: zonesData, isFetching: zonesLoading } =
		useGetPOIZonesByParentIdQuery(
			{ id: projectId },
			{
				skip: !projectId,
			},
		);

	const zoneOptions =
		zonesData?.data?.map((i) => ({
			label: i.name,
			value: i.id,
		})) || [];

	return (
		<InputSelect
			id="assignedZones"
			name="assignedZones"
			label={trans("Assigned Zones")}
			placeholder={
				!projectId
					? trans("Select project first")
					: zonesLoading
						? trans("Loading zones...")
						: trans("Select zones")
			}
			options={zoneOptions}
			value={value}
			onChange={onChange}
			mode="multiple"
			maxTagCount="responsive"
			disabled={!projectId || zonesLoading}
			errors={errors}
			touched={touched}
			formstyle="form-input select-z-index"
		/>
	);
};

const roleOptions = [
	{ label: trans("Contractor"), value: "CONTRACTOR" },
	{ label: trans("Sub-Contractor"), value: "SUB_CONTRACTOR" },
	{ label: trans("QA"), value: "QA" },
	{ label: trans("Consultant"), value: "CONSULTANT" },
];

export default function AssignProjectsPopup() {
	const {
		isAssigningProjects,
		setIsAssigningProjects,
		activeCompany,
		currentCompanyProjectId,
		isEditing,
		setIsEditing,
		setcurrentCompanyProjectId,
		setReloadData,
	} = useContext(popupcontext);
	const { data: companyTradesData, isFetching: companyTradesLoading } =
		useGetCompanyTradesQuery();
	const { data: CompanyProjectTradesData } = useGetCompanyProjectTradesQuery(
		currentCompanyProjectId,
		{ skip: !currentCompanyProjectId, refetchOnMountOrArgChange: true },
	);
	const [assignProjects, { isLoading }] = useAssignProjectsMutation();
	const [editCompanyAssignProjects, { isLoading: editLoading }] =
		useEditCompanyAssignedProjectsMutation();
	const { data: projectsData } = useGetPOIProjectsQuery();
	console.log("project data", projectsData?.data);
	const initialValues = {
		projects: [
			{
				projectId: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.projectId
					: "",
				// role: "",
				trades: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.trades
					: "",
				contractValue: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.contractValue
					: "",
				contractNumber: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.contractNumber
					: "",
				startDate: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.startDate
					: "",
				endDate: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.endDate
					: "",
				slaResponseTime: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.slaResponseTime
					: "",
				slaCompletionTime: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.slaCompletionTime
					: "",
				assignedZones: currentCompanyProjectId
					? CompanyProjectTradesData?.data?.assignedZones
					: "",
			},
		],
	};

	const validationSchema = Yup.object({
		projects: Yup.array()
			.of(
				Yup.object({
					projectId: Yup.number()
						.required(trans("Project is required"))
						.positive(trans("Project must be selected")),
					// role: Yup.string().required(trans("Role is required")),
					trades: Yup.array()
						.min(1, trans("At least one trade is required"))
						.required(trans("Trades are required")),
					contractValue: Yup.number()
						.required(trans("Contract value is required"))
						.positive(trans("Contract value must be positive")),
					contractNumber: Yup.string().required(
						trans("Contract number is required"),
					),
					startDate: Yup.string().required(trans("Start date is required")),
					endDate: Yup.string().required(trans("End date is required")),
					slaResponseTime: Yup.number()
						.required(trans("SLA Response Time is required"))
						.positive(trans("SLA Response Time must be positive")),
					slaCompletionTime: Yup.number()
						.required(trans("SLA Completion Time is required"))
						.positive(trans("SLA Completion Time must be positive")),
					assignedZones: Yup.array()
						.min(1, trans("At least one zone is required"))
						.required(trans("Assigned zones are required")),
				}),
			)
			.min(1, trans("At least one project is required")),
	});
	const toAssignProjectPayload = (p) => ({
		status: p?.status || "ACTIVE",
		contractValue: Number(p?.contractValue) || 0,
		contractNumber: p?.contractNumber || "",
		startDate: p?.startDate ? String(p.startDate).slice(0, 10) : null,
		endDate: p?.endDate ? String(p.endDate).slice(0, 10) : null,
		slaResponseTime: Number(p?.slaResponseTime) || 0,
		slaCompletionTime: Number(p?.slaCompletionTime) || 0,

		assignedZones: (p?.assignedZones || []).map((z) => z?.id ?? z),

		tradeIds: (p?.trades || []).map((t) => t?.tradeId ?? t),
	});

	const handleSubmit = async (values, { resetForm }) => {
		try {
			const payload = {
				projects: values.projects.map((project) => ({
					projectId: Number(project.projectId),
					// role: project.role,
					trades: project.trades,
					contractValue: Number(project.contractValue),
					contractNumber: project.contractNumber,
					startDate: project.startDate,
					endDate: project.endDate,
					slaResponseTime: Number(project.slaResponseTime),
					slaCompletionTime: Number(project.slaCompletionTime),
					assignedZones: project.assignedZones.map((zone) => Number(zone)),
				})),
			};

			console.log("Values", values?.projects[0]);
			console.log(
				"Process Values",
				toAssignProjectPayload(values?.projects[0]),
			);
			if (!currentCompanyProjectId) {
				await assignProjects({
					companyId: activeCompany?.id,
					payload,
				}).unwrap();
				toast.success(trans("Projects assigned successfully"), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar",
				});
			} else {
				await editCompanyAssignProjects({
					companyId: CompanyProjectTradesData?.data?.companyId,
					projectId: CompanyProjectTradesData?.data?.projectId,
					payload: toAssignProjectPayload(values?.projects?.[0]),
				}).unwrap();
				await setReloadData(true);
				toast.success(trans("Project edited successfully"), {
					autoClose: 800,
					rtl: localStorage.getItem("lang") === "ar",
				});
			}

			resetForm();
			setIsAssigningProjects(false);
		} catch (error) {
			!currentCompanyProjectId
				? toast.error(
						error?.data?.message || trans("Failed to assign projects"),
						{
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar",
						},
					)
				: toast.error(error?.data?.message || trans("Failed to edit project"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar",
					});
		}
		setIsAssigningProjects(false);
		setIsEditing(false);
		setcurrentCompanyProjectId(null);
	};

	return (
		<Popup show={isAssigningProjects} size="lg" style={"popup-bodyscroll"}>
			<Modal.Header className="modal-header flex-between">
				<Modal.Title id="contained-modal-title-vcenter">
					<PopupTitle
						headtitle={
							!currentCompanyProjectId
								? trans("Assign Projects")
								: trans("Edit Project")
						}
					/>
				</Modal.Title>
				<Close
					style="close-32 close"
					close={() => {
						setIsAssigningProjects(false);
						setcurrentCompanyProjectId(null);
					}}
					img="Close"
				/>
			</Modal.Header>
			<Modal.Body className="modal-body-scrollable">
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ values, setFieldValue, errors, touched }) => (
						<FormFormik>
							{console.log("vla", values)}
							<div className="form-section">
								{values.projects.map((project, index) => (
									<div
										key={index}
										className="mb-4 p-3 border rounded"
										style={{ backgroundColor: "#f9f9f9" }}
									>
										<div className="d-flex justify-content-between align-items-center mb-3">
											<h6 className="mb-0">
												{trans("Project")} {index + 1}
											</h6>
											{values.projects.length > 1 && (
												<Button
													type="default"
													htmlType="button"
													onClick={() => {
														const newProjects = values.projects.filter(
															(_, i) => i !== index,
														);
														setFieldValue("projects", newProjects);
													}}
													danger
												>
													<DeleteOutlined /> {trans("Remove")}
												</Button>
											)}
										</div>

										<div className="row">
											<div className="col-lg-6 mb-3">
												<InputSelect
													id={`projects.${index}.projectId`}
													name={`projects.${index}.projectId`}
													label={trans("Project")}
													placeholder={trans("Select project")}
													options={
														projectsData?.data?.map((i) => ({
															label: i.name,
															value: i.id,
														})) || []
													}
													value={project.projectId}
													onChange={(val) => {
														setFieldValue(`projects.${index}.projectId`, val);
														// Clear zones when project changes
														setFieldValue(
															`projects.${index}.assignedZones`,
															[],
														);
													}}
													errors={
														errors.projects?.[index]?.projectId &&
														touched.projects?.[index]?.projectId
															? errors.projects[index].projectId
															: undefined
													}
													touched={
														touched.projects?.[index]?.projectId || false
													}
													formstyle="form-input select-z-index"
													disabled={currentCompanyProjectId}
												/>
											</div>

											{/* <div className="col-lg-6 mb-3">
												<InputSelect
													id={`projects.${index}.role`}
													name={`projects.${index}.role`}
													label={trans("Role")}
													placeholder={trans("Select role")}
													options={roleOptions}
													value={project.role}
													onChange={(val) =>
														setFieldValue(`projects.${index}.role`, val)
													}
													errors={
														errors.projects?.[index]?.role &&
														touched.projects?.[index]?.role
															? errors.projects[index].role
															: undefined
													}
													touched={touched.projects?.[index]?.role || false}
													formstyle="form-input select-z-index"
												/>
											</div> */}

											<div className="col-lg-6 mb-3">
												<InputSelect
													id={`projects.${index}.trades`}
													name={`projects.${index}.trades`}
													label={trans("Trades")}
													placeholder={trans("Select trades")}
													options={companyTradesData?.data?.map((i) => ({
														label: i.name,
														value: i.id,
													}))}
													value={(project?.trades || []).map(
														(i) => i.tradeId ?? i,
													)}
													onChange={(val) =>
														setFieldValue(`projects.${index}.trades`, val || [])
													}
													mode="multiple"
													maxTagCount="responsive"
													errors={
														errors.projects?.[index]?.trades &&
														touched.projects?.[index]?.trades
															? errors.projects[index].trades
															: undefined
													}
													touched={touched.projects?.[index]?.trades || false}
													formstyle="form-input select-z-index"
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("Contract Value")}
													type="number"
													placeholder={trans("Enter contract value")}
													formstyle="form-input"
													name={`projects.${index}.contractValue`}
													value={project.contractValue}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.contractValue`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.contractValue &&
														touched.projects?.[index]?.contractValue
															? errors.projects[index].contractValue
															: undefined
													}
													touched={
														touched.projects?.[index]?.contractValue || false
													}
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("Contract Number")}
													type="text"
													placeholder={trans("Enter contract number")}
													formstyle="form-input"
													name={`projects.${index}.contractNumber`}
													value={project.contractNumber}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.contractNumber`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.contractNumber &&
														touched.projects?.[index]?.contractNumber
															? errors.projects[index].contractNumber
															: undefined
													}
													touched={
														touched.projects?.[index]?.contractNumber || false
													}
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("Start Date")}
													type="date"
													placeholder={trans("Select start date")}
													formstyle="form-input"
													name={`projects.${index}.startDate`}
													value={
														project.startDate
															? project.startDate.slice(0, 10)
															: ""
													}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.startDate`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.startDate &&
														touched.projects?.[index]?.startDate
															? errors.projects[index].startDate
															: undefined
													}
													touched={
														touched.projects?.[index]?.startDate || false
													}
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("End Date")}
													type="date"
													placeholder={trans("Select end date")}
													formstyle="form-input"
													name={`projects.${index}.endDate`}
													value={
														project.endDate ? project.endDate.slice(0, 10) : ""
													}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.endDate`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.endDate &&
														touched.projects?.[index]?.endDate
															? errors.projects[index].endDate
															: undefined
													}
													touched={touched.projects?.[index]?.endDate || false}
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("SLA Response Time")}
													type="number"
													placeholder={trans("Enter SLA response time (hours)")}
													formstyle="form-input"
													name={`projects.${index}.slaResponseTime`}
													value={project.slaResponseTime}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.slaResponseTime`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.slaResponseTime &&
														touched.projects?.[index]?.slaResponseTime
															? errors.projects[index].slaResponseTime
															: undefined
													}
													touched={
														touched.projects?.[index]?.slaResponseTime || false
													}
												/>
											</div>

											<div className="col-lg-6 mb-3">
												<Input
													title={trans("SLA Completion Time")}
													type="number"
													placeholder={trans(
														"Enter SLA completion time (hours)",
													)}
													formstyle="form-input"
													name={`projects.${index}.slaCompletionTime`}
													value={project.slaCompletionTime}
													onChange={(e) =>
														setFieldValue(
															`projects.${index}.slaCompletionTime`,
															e.target.value,
														)
													}
													errors={
														errors.projects?.[index]?.slaCompletionTime &&
														touched.projects?.[index]?.slaCompletionTime
															? errors.projects[index].slaCompletionTime
															: undefined
													}
													touched={
														touched.projects?.[index]?.slaCompletionTime ||
														false
													}
												/>
											</div>

											<div className="col-lg-12 mb-3">
												<ProjectZonesSelect
													projectId={project.projectId}
													value={(project?.assignedZones || []).map(
														(i) => i.id ?? i,
													)}
													onChange={(zones) =>
														setFieldValue(
															`projects.${index}.assignedZones`,
															zones,
														)
													}
													errors={
														errors.projects?.[index]?.assignedZones &&
														touched.projects?.[index]?.assignedZones
															? errors.projects[index].assignedZones
															: undefined
													}
													touched={
														touched.projects?.[index]?.assignedZones || false
													}
												/>
											</div>
										</div>
									</div>
								))}

								{!currentCompanyProjectId && (
									<div className="mb-3">
										<Button
											type="default"
											htmlType="button"
											onClick={() => {
												setFieldValue("projects", [
													...values.projects,
													{
														projectId: "",
														role: "",
														trades: [],
														contractValue: "",
														contractNumber: "",
														startDate: "",
														endDate: "",
														slaResponseTime: "",
														slaCompletionTime: "",
														assignedZones: [],
													},
												]);
											}}
										>
											<PlusOutlined /> {trans("Add Project")}
										</Button>
									</div>
								)}
							</div>

							<div className="offcanvas-footer m-0 w-100">
								<div className="offcanvas-footer-body gap-8">
									<Button
										type="primary"
										htmlType="submit"
										loading={isLoading}
										disabled={isLoading}
									>
										{isLoading || editLoading
											? currentCompanyProjectId
												? trans("Editing...")
												: trans("Assigning...")
											: currentCompanyProjectId
												? trans("Edit Project")
												: trans("Assign Projects")}
									</Button>
									<Button
										type="default"
										htmlType="button"
										disabled={isLoading}
										onClick={() => {
											setIsAssigningProjects(false);
											setcurrentCompanyProjectId(null);
										}}
									>
										{trans("Cancel")}
									</Button>
								</div>
							</div>
						</FormFormik>
					)}
				</Formik>
			</Modal.Body>
		</Popup>
	);
}
